import React, { ReactNode } from 'react';
import { FieldRenderProps } from 'react-final-form';
import { fuzzysearch } from './fuzzysearch';
import { AutocompleteSource } from './AutocompleteSource';

type Props = FieldRenderProps<string> & {
  placeholder: string;
  selectSources: AutocompleteSource[];
  required?: boolean;
  onChangeInputValue?: (e) => void;
  onChangeSelectSources?: (e) => void;
  customError?: string;
  onBlur?: (lang: string) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onEnterSelect?: (event: React.KeyboardEvent<HTMLInputElement>, label: string) => void
};

type State = {
  selectedButton: HTMLButtonElement,
};

class AutocompleteInput extends React.Component<Props, State> {
  state: State = {
    selectedButton: null,
  };

  resultsRef = React.createRef<HTMLDivElement>();

  inputRef = React.createRef<HTMLInputElement>();

  onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case ('Enter'):
        if (this.state.selectedButton) {
          if (this.props.onEnterSelect) {
            this.props.onEnterSelect(event, this.state.selectedButton.textContent);
          } else {
            this.state.selectedButton.click();
          }
          this.setState({ selectedButton: null });
        } else if (this.props.onKeyDown) {
          this.props.onKeyDown(event);
        }
        break;
      case ('ArrowDown'):
        if (this.state.selectedButton?.nextElementSibling) {
          this.setState((prevState) => ({
            selectedButton: prevState.selectedButton.nextElementSibling as HTMLButtonElement,
          }), () => {
            this.state.selectedButton.scrollIntoView({ block: 'nearest' });
          });
        } else if (this.resultsRef.current?.firstElementChild) {
          this.setState({
            selectedButton: this.resultsRef.current.firstElementChild as HTMLButtonElement,
          }, () => {
            this.state.selectedButton.scrollIntoView({ block: 'nearest' });
          });
        }
        if (this.props.onKeyDown) this.props.onKeyDown(event);
        break;
      case ('ArrowUp'):
        if (this.state.selectedButton?.previousElementSibling) {
          this.setState((prevState) => ({
            selectedButton: prevState.selectedButton.previousElementSibling as HTMLButtonElement,
          }), () => {
            this.state.selectedButton.scrollIntoView({ block: 'nearest' });
          });
        } else if (this.resultsRef.current?.lastElementChild) {
          this.setState({
            selectedButton: this.resultsRef.current.lastElementChild as HTMLButtonElement,
          }, () => {
            this.state.selectedButton.scrollIntoView({ block: 'nearest' });
          });
        }
        if (this.props.onKeyDown) this.props.onKeyDown(event);
        break;
      default:
        if (this.props.onKeyDown) this.props.onKeyDown(event);
    }
  };

  select = (searchWord: AutocompleteSource): void => {
    this.props.input.onChange(searchWord.label);
    if (this.props.onChangeInputValue) {
      this.props.onChangeInputValue(searchWord.id);
    }
    this.inputRef.current.blur();
  };

  render(): ReactNode {
    const htmlFor = this.props.input.name;
    return (
      <div className={`autocomplete-input ${this.props.meta.touched && (this.props.meta.error || this.props.customError) ? 'input-error' : ''}`}>
        <div className={`text-input-group ${this.props.meta.touched && (this.props.meta.error || this.props.customError) ? 'input-error' : ''}`}>
          <input
            ref={this.inputRef}
            className="text-input-field"
            value={this.props.input.value}
            onChange={(e) => {
              this.setState({ selectedButton: null });
              this.props.input.onChange(e);
              if (this.props.onChangeSelectSources) {
                this.props.onChangeSelectSources(e);
              }
              if (this.props.onChangeInputValue
              && this.props.selectSources
                && this.props.selectSources.filter((s) => s.label === e.target.value).length > 0) {
                this.props.onChangeInputValue(this.props.selectSources.filter((s) =>
                  s.label === e.target.value)[0].id);
              }
            }}
            onBlur={(e) => {
              this.props.input.onBlur(e);
              this.setState({ selectedButton: null }, () => {
                if (this.resultsRef.current) {
                  this.resultsRef.current.scrollTop = 0;
                }
              });
              if (this.props.onBlur) {
                this.props.onBlur(e.target.value);
              }
            }}
            onFocus={this.props.onFocus}
            placeholder={this.props.placeholder}
            id={htmlFor}
            required={this.props.required}
            autoComplete="nope"
            onKeyDown={this.onKeyDown}
          />
          <label htmlFor={htmlFor} className={`text-input-placeholder ${this.props.required && 'text-input-placeholder--mandatory'}`}>{this.props.placeholder}</label>
          {
          this.props.input.value && (
          <>
            <button
              className="text-input-button-container"
              onClick={() => this.props.input.onChange(null)}
              type="button"
            >
              <i className="fa fa-times" />
            </button>
            {
                this.props.selectSources && this.props.selectSources.length > 0
                && (
                <div ref={this.resultsRef} className="autocomplete-results">
                  {this.props.selectSources.filter((value) => {
                    if (this.props.input.value && this.props.input.value !== '' && value && value.label) {
                      return fuzzysearch(this.props.input.value, value.label);
                    }
                    return true;
                  }).sort((a, b) => a.label.localeCompare(b.label))
                    .map((value) => (
                      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
                      <button
                        key={value.id}
                        type="button"
                        className={`result ${this.state.selectedButton?.textContent === value.label ? ' focused-result' : ''}`}
                        onClick={(e) => {
                          this.select(value);
                          if (this.props.onBlur) {
                            this.props.onBlur(value.label);
                          }
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseOver={(e) => {
                          this.setState({ selectedButton: e.currentTarget });
                        }}
                      >
                        {value.label}
                      </button>
                    ))}
                </div>
                )
            }
          </>
          )
        }
          {this.props.children}
        </div>
        {
          this.props.meta.touched && (
            (this.props.customError && <span className="text-error">{this.props.customError}</span>)
            || (this.props.meta.error && <span className="text-error">{this.props.meta.error}</span>)
          )
        }
      </div>
    );
  }
}

export default AutocompleteInput;
